<template>
  <label
    class="rounded-md border p-2 py-1.5 flex items-center justify-between gap-3 cursor-pointer hover:border-primary duration-300"
    :class="selected ? 'border-primary' : 'dark:border-surface-700'"
  >
    <span
      class="grid shrink-0 place-content-center bg-primary text-surface-0 h-8 w-8 sm:h-9 sm:w-9 rounded-md"
    >
      <svg
        class="shrink-0 fill-current"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        height="24"
        width="24"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 22v-5M9 8V2m6 6V2m3 6v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"
        />
      </svg>
    </span>
    <div class="grow">
      <p class="font-medium leading-snug">
        {{ displayName }}
      </p>
      <div class="flex items-center gap-1.5">
        <span
          class="bg-surface-200 grid h-5 w-5 place-content-center rounded-full overflow-hidden dark:bg-surface-700"
        >
          <i class="pi pi-user text-xs"></i>
        </span>
        <p class="text-sm opacity-80">{{ activeAccountName }}</p>
      </div>
    </div>

    <input
      v-model="selected"
      type="checkbox"
      class="form-checkbox duration-300"
      name="integration"
    />
  </label>
</template>

<script setup lang="ts">
import type {
  IFacebookData,
  IntegrationTypes,
  ITeamsData,
  ITikTokData,
  IUserIntegration,
  IZapierIntegrationData,
} from "@/types/account";

const props = defineProps<{
  platform: IntegrationTypes;
  userIntegration: IUserIntegration<IntegrationTypes>;
}>();

const selected = defineModel<boolean>();

const displayName = computed(() => {
  if (props.platform === "tiktok") {
    const tiktokData = props.userIntegration.data as ITikTokData;
    return (
      tiktokData.profile?.creator_nickname ||
      props.userIntegration.name ||
      props.userIntegration.id
    );
  }
  return props.userIntegration.name || props.userIntegration.id || props.platform;
});

const activeAccountName = computed(() => {
  if (!props.userIntegration?.data) return "n/a";
  const integrationData = props.userIntegration.data as any;
  switch (props.platform) {
    case "twitter":
      return integrationData.profile?.name || "n/a";
    case "instagram":
    case "facebook":
      return (
        integrationData.pages?.find(
          (p: IFacebookData["pages"][0]) => p.pageId === integrationData.pageId
        )?.name || "n/a"
      );
    case "linkedin":
      return integrationData.profile?.name || "n/a";
    case "wordpress":
      return integrationData.profile?.username || "n/a";
    case "email-marketing":
      return integrationData.emailAddress || "n/a";
    case "email":
      return integrationData.targetEmail || integrationData.emailAddress || "n/a";
    case "zapier":
      return (
        ((integrationData as IZapierIntegrationData).webHooks?.length || 0) + " webhooks"
      );
    case "teams":
      return (integrationData as ITeamsData).outgoingUrl || "n/a";
    case "tiktok": {
      const tiktokData = props.userIntegration.data as ITikTokData;
      return tiktokData.profile?.creator_username
        ? `@${tiktokData.profile.creator_username}`
        : "n/a";
    }
    // case "google-analytics":

    default:
      return "n/a";
  }
});
</script>
